/**
 * CR-6403: the Supplier Performance filter body, grouped into the four sections
 * from the supplied design — Date Range · Company · Supplier · Classification.
 *
 * Every control here is the SAME element bound to the SAME handler it had when
 * it lived inline in SupplierPerformanceFilters: only the grouping and order
 * change. The ticket is explicit that the diagram is AI-generated and may
 * misrepresent input types, and that the existing cascades must not regress, so
 * nothing about the inputs or their callbacks has been altered:
 *   - division → company site      (scopedDivisions / viewFilteredSites)
 *   - site → supplier              (supplierSites)
 *   - supplier country → supplier site
 *   - commodity group → commodity type (the onChange still resets the type)
 *
 * CR-6403 rework 2: the sections now use the app-wide `FilterSection` /
 * `FilterGrid` / `FilterField` shell — the same one the Dashboard, the
 * Purchasing tabs and Quality render — instead of a private heading style, so
 * the panel is blocked out identically to every other filter surface (icon +
 * shaded section bar, padded body, 4-up grid, one label style). The
 * CR-4802 tenant visibility gates (`fv.*`) are untouched: a control that was
 * hidden for a tenant before is hidden in exactly the same condition now.
 *
 * Extracted into its own file so the parent stays under the 500-line limit.
 */
import { Building2, CalendarRange, Tags, Truck } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import SearchableSelect from '../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField } from '../../filters/FilterSection';
import type {
  Site, CompanyGroup, Warehouse, SupplierRatingThreshold, CommodityGroup, CommodityType,
} from '../../../lib/api';
import type { SupplierPerformanceFilterValues } from '../SupplierPerformanceFilters';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { bandRange } from '../../../pages/supplier-performance/utils';
import { useFilterVisibility } from '../../../hooks/useFilterVisibility';

/**
 * Horizontal divider with padding so it doesn't touch card edges.
 *
 * CR-6403 rework 3: QA bounced the panel again because its sections were still
 * blocked out differently from the Purchasing tabs and Quality. Moving onto the
 * shared `FilterSection` shell (rework 2) was not enough — every other sectioned
 * panel (ASN, Deliveries, Forecast, PO Lines, PO Headers, Pricing, NCR) cancels
 * that shell's own full-bleed `border-t` with `[&>section]:border-t-0` and draws
 * this inset rule between sections instead, including one above the first. This
 * panel was the only one left using the shell's edge-to-edge line, which is the
 * spacing difference the screenshots show.
 */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

export interface FilterSectionsProps {
  localFilters: SupplierPerformanceFilterValues;
  setLocalFilters: React.Dispatch<React.SetStateAction<SupplierPerformanceFilterValues>>;
  updateFilter: <K extends keyof SupplierPerformanceFilterValues>(
    key: K, value: SupplierPerformanceFilterValues[K],
  ) => void;
  applyAndUpdate: <K extends keyof SupplierPerformanceFilterValues>(
    key: K, value: SupplierPerformanceFilterValues[K],
  ) => void;
  scopedDivisions: CompanyGroup[];
  isDivisionLocked: boolean;
  /** CR-6403: sets Division and drops a Company Site that is no longer in it. */
  /** `apply` = the pick was a clear (the x) and must reach the page at once. */
  onDivisionChange: (val: string, apply?: boolean) => void;
  viewFilteredSites: Site[];
  isSiteLocked: boolean;
  supplierGroups: CompanyGroup[];
  /**
   * CR-6403: the FULL supplier list. Used only to build the Supplier Country
   * options — deriving those from the narrowed list would collapse the country
   * dropdown to whichever country is already selected.
   */
  supplierSites: Array<Site & { company_name?: string }>;
  /**
   * CR-6403: the supplier list after the Company Site and Supplier Country
   * cascades have been applied. Drives the Supplier Site dropdown only.
   */
  supplierSiteOptions: Array<Site & { company_name?: string }>;
  warehouses: Warehouse[];
  commodityGroups: CommodityGroup[];
  commodityTypes: CommodityType[];
  ratingThresholds: SupplierRatingThreshold[];
  supplierTypes: ReadonlyArray<{ value: string; label: string }>;
  /** The quick-range button row; sits beside the From/To dates as on the dashboard. */
  quickRanges: React.ReactNode;
}

/** The dashboard's Historical / Live segmented control, one half each. */
const SEGMENT_ON = 'bg-primary text-primary-foreground border-primary';
const SEGMENT_OFF = 'bg-background text-muted-foreground border-border hover:bg-muted';

export default function FilterSections({
  localFilters,
  setLocalFilters,
  updateFilter,
  applyAndUpdate,
  scopedDivisions,
  isDivisionLocked,
  onDivisionChange,
  viewFilteredSites,
  isSiteLocked,
  supplierGroups,
  supplierSites,
  supplierSiteOptions,
  warehouses,
  commodityGroups,
  commodityTypes,
  ratingThresholds,
  supplierTypes,
  quickRanges,
}: FilterSectionsProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const { warehouseModuleEnabled } = useMenuSettings();
  // A scope the top-bar view context has pinned: shown, not pickable — the
  // same locked cell the dashboard's Company section renders.
  const lockedCell = (label: string) => (
    <div
      className="w-full px-3 py-2.5 border border-border rounded-md bg-muted/40 text-muted-foreground cursor-not-allowed"
      title="Locked by current view level"
    >
      {label}
    </div>
  );

  // CR-4802: distinct Company Country values across the view-scoped sites —
  // drives both the option list and whether the filter shows at all.
  const siteCountryValues = Array.from(
    new Set(viewFilteredSites.map((s) => s.country).filter(Boolean)),
  ).sort() as string[];

  return (
    // `[&>section]:border-t-0` cancels FilterSection's own full-bleed rule so
    // the inset SectionDividers above are the only separators — the same
    // wrapper ASN, Deliveries, PO Lines and NCR use.
    <div className="[&>section]:border-t-0">
      {/* ---------------- Date Range ---------------- */}
      <SectionDivider />
      <FilterSection title="Date Range" icon={CalendarRange}>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex flex-wrap gap-2 max-w-2xl">{quickRanges}</div>
          <div className="flex flex-wrap gap-4">
            <div className="w-44">
              <FilterField label="From Date" htmlFor="dateFrom">
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                />
              </FilterField>
            </div>
            <div className="w-44">
              <FilterField label="To Date" htmlFor="dateTo">
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                />
              </FilterField>
            </div>
          </div>
        </div>
        {/* Data mode sits with the date controls per the design's DATE RANGE block.
            W3-T9: it applies on click like the dashboard's switch (and like the
            quick ranges here, CR-4783) — a Live-selected switch over Historical
            figures is exactly what the switch exists to prevent. */}
        {fv.historicalLive && (
          <div className="flex items-center gap-3 mt-4">
            <span className="text-sm font-medium text-foreground">Data</span>
            <div className="flex">
              <button
                type="button"
                onClick={() => applyAndUpdate('dataMode', 'historical')}
                className={`px-4 py-2 text-sm font-medium rounded-l-md border transition-colors ${
                  localFilters.dataMode === 'historical' ? SEGMENT_ON : SEGMENT_OFF
                }`}
              >
                Historical
              </button>
              <button
                type="button"
                onClick={() => applyAndUpdate('dataMode', 'live')}
                className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b transition-colors ${
                  localFilters.dataMode === 'live' ? SEGMENT_ON : SEGMENT_OFF
                }`}
              >
                Live
              </button>
            </div>
          </div>
        )}
      </FilterSection>

      {/* ---------------- Company ---------------- */}
      <SectionDivider />
      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          {fv.division && scopedDivisions.length > 0 && (
            <FilterField label="Division">
              {isDivisionLocked ? lockedCell(scopedDivisions[0]?.name || 'Selected division') : (
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Divisions' }, ...scopedDivisions.map((d) => ({ value: d.id.toString(), label: d.name }))]}
                  value={localFilters.division}
                  // CR-7264 rework: the x on a select is a clear, and a clear applies at once.
                  onChange={(val) => onDivisionChange(val || 'all', !val)}
                  placeholder="All Divisions"
                />
              )}
            </FilterField>
          )}
          {viewFilteredSites.length > 0 && (
            <FilterField label="Company Site">
              {isSiteLocked ? lockedCell(viewFilteredSites[0]?.name || 'Selected site') : (
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Company Sites' }, ...viewFilteredSites.map((s) => ({ value: s.id.toString(), label: s.name }))]}
                  value={localFilters.site}
                  onChange={(val) => (val ? updateFilter('site', val) : applyAndUpdate('site', 'all'))}
                  placeholder="All Company Sites"
                />
              )}
            </FilterField>
          )}
          {/* CR-4802: hidden when every accessible site shares one country —
              nothing for the filter to narrow. Mirrors the Dashboard gate. */}
          {fv.companyCountry && siteCountryValues.length > 1 && (
            <FilterField label="Company Country">
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Countries' },
                  ...siteCountryValues.map((c) => ({ value: c, label: c })),
                ]}
                value={localFilters.siteCountry}
                onChange={(val) => applyAndUpdate('siteCountry', val || 'all')}
                placeholder="All Countries"
              />
            </FilterField>
          )}
          {/* CR-6312 rework: the warehouse filter renders only where the
              tenant has switched the warehouse module on (the admin-form
              toggle that previously gated nothing). Still not gated on
              warehouses.length (CR-4802): an empty list is a data state,
              not a feature state. */}
          {fv.warehouse && warehouseModuleEnabled && (
            <FilterField label="Warehouse" htmlFor="warehouse">
              <Select
                value={localFilters.warehouse}
                onValueChange={(value) => updateFilter('warehouse', value)}
              >
                <SelectTrigger id="warehouse">
                  <SelectValue placeholder="All Warehouses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouses</SelectItem>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id.toString()}>
                      {warehouse.name} ({warehouse.site_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
          )}
        </FilterGrid>
      </FilterSection>

      {/* ---------------- Supplier ---------------- */}
      <SectionDivider />
      <FilterSection title="Supplier" icon={Truck}>
        <FilterGrid>
          <FilterField label="Supplier Group">
            <SearchableSelect
              options={[{ value: 'all', label: 'All Supplier Groups' }, ...supplierGroups.map((g) => ({ value: g.id.toString(), label: g.name }))]}
              value={localFilters.supplierGroup}
              onChange={(val) => (val ? updateFilter('supplierGroup', val) : applyAndUpdate('supplierGroup', 'all'))}
              placeholder="All Supplier Groups"
            />
          </FilterField>
          <FilterField label="Supplier Site">
            <SearchableSelect
              // CR-6403: every option used to render as "Name (undefined)".
              // Neither path that populates supplierSites sets company_name —
              // the Helix branch stores raw Company records and the non-Helix
              // branch maps to {id, name} — so the parenthetical was always
              // undefined. Show it only when there is actually a company to
              // show, which is what the template intended.
              // CR-6403: supplierSiteOptions, not supplierSites — this list is
              // narrowed by the Company Site and Supplier Country cascades.
              options={[
                { value: 'all', label: 'All Supplier Sites' },
                ...supplierSiteOptions.map((s) => ({
                  value: s.id.toString(),
                  label: s.company_name ? `${s.name} (${s.company_name})` : s.name,
                })),
              ]}
              value={localFilters.supplierSite}
              onChange={(val) => (val ? updateFilter('supplierSite', val) : applyAndUpdate('supplierSite', 'all'))}
              placeholder="All Supplier Sites"
            />
          </FilterField>
          {fv.supplierType && (
            <FilterField label="Supplier Type" htmlFor="supplierType">
              <Select
                value={localFilters.supplierType}
                onValueChange={(value) => updateFilter('supplierType', value)}
              >
                <SelectTrigger id="supplierType">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {supplierTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
          )}
          <FilterField label="Supplier Country">
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Countries' },
                ...Array.from(new Set(supplierSites.map((s) => s.country).filter(Boolean)))
                  .sort()
                  .map((c) => ({ value: c as string, label: c as string })),
              ]}
              value={localFilters.supplierCountry}
              onChange={(val) => applyAndUpdate('supplierCountry', val || 'all')}
              placeholder="All Countries"
            />
          </FilterField>
        </FilterGrid>
        {/* Under the grid, as the dashboard places its tickbox. */}
        <div className="flex items-center gap-2 mt-4 w-fit">
          <Checkbox
            id="strategic"
            checked={localFilters.strategic}
            onCheckedChange={(checked) => updateFilter('strategic', checked as boolean)}
          />
          <Label htmlFor="strategic" className="text-sm font-medium cursor-pointer">
            Strategic suppliers only
          </Label>
        </div>
        {/* CR-7264 rework: "Hide Child Companies" was removed here — it had
            no consumer anywhere (a placeholder since the panel was built),
            and a company has no parent to hide by. */}
      </FilterSection>

      {/* ---------------- Classification ---------------- */}
      <SectionDivider />
      <FilterSection title="Classification" icon={Tags}>
        <FilterGrid>
          {/* CR-4802: hidden together when the tenant hasn't configured any
              commodity groups in Admin Setup — mirrors the same gate on the
              Dashboard and NCR filter panels. commodityGroups is the full
              tenant list, not narrowed by any current selection. */}
          {fv.commodity && commodityGroups.length > 0 && (
            <FilterField label="Commodity Group" htmlFor="commodityGroup">
              <Select
                value={localFilters.commodityGroup}
                onValueChange={(value) => {
                  updateFilter('commodityGroup', value);
                  // Reset commodity type when group changes
                  updateFilter('commodityType', 'all');
                }}
              >
                <SelectTrigger id="commodityGroup">
                  <SelectValue placeholder="All Commodities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Commodities</SelectItem>
                  {commodityGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id.toString()}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
          )}
          {fv.commodity && commodityGroups.length > 0 && (
            <FilterField label="Commodity Type" htmlFor="commodityType">
              {/* CR-6403 rework 3: never disabled. The Purchasing panels
                  (DeliveryFilterCustomerLayout.commodityTypesForSelection) keep
                  this control live and simply list every type until a group
                  narrows it — disabling it here made Supplier Performance the
                  odd one out and blocked the legitimate "any group, this type"
                  query. The option filter below already falls through to the
                  full list when the group is 'all', so only the guard goes. */}
              <Select
                value={localFilters.commodityType}
                onValueChange={(value) => updateFilter('commodityType', value)}
              >
                <SelectTrigger id="commodityType">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {commodityTypes
                    .filter((type) => localFilters.commodityGroup === 'all' || type.commodity_group.toString() === localFilters.commodityGroup)
                    .map((type) => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FilterField>
          )}
          {/* CR-7273: Order Type popover removed — the column it filtered is dormant. */}
          <FilterField label="Rating" htmlFor="rating">
            <Select
              value={localFilters.rating}
              onValueChange={(value) => {
                if (value === 'N/A') {
                  setLocalFilters((prev) => ({ ...prev, rating: value, excludeNARatings: false }));
                } else {
                  updateFilter('rating', value);
                }
              }}
            >
              <SelectTrigger id="rating">
                <SelectValue placeholder="All Ratings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                {ratingThresholds.length > 0 ? (
                  // Customer review 11 Sep (item 2): the configured Grade
                  // Thresholds — label and range — for every role (useRatingBands).
                  ratingThresholds.map((threshold) => (
                    <SelectItem key={threshold.id} value={threshold.grade}>
                      {threshold.display_label || threshold.label || threshold.grade}
                      <span className="ml-1 text-muted-foreground">({bandRange(threshold)})</span>
                    </SelectItem>
                  ))
                ) : (
                  <>
                    <SelectItem value="A">A (90-100%)</SelectItem>
                    <SelectItem value="B">B (75-90%)</SelectItem>
                    <SelectItem value="C">C (60-75%)</SelectItem>
                    <SelectItem value="D">D (40-60%)</SelectItem>
                    <SelectItem value="F">F (0-40%)</SelectItem>
                  </>
                )}
                <SelectItem value="N/A">N/A (No Data)</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </FilterGrid>
        <div className="flex items-center gap-2 mt-4 w-fit">
          <Checkbox
            id="excludeNARatings"
            checked={localFilters.excludeNARatings}
            onCheckedChange={(checked) => updateFilter('excludeNARatings', checked as boolean)}
          />
          <Label htmlFor="excludeNARatings" className="text-sm font-medium cursor-pointer">
            Exclude N/A Ratings
          </Label>
        </div>
      </FilterSection>
    </div>
  );
}
